import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Clock, Calendar, ArrowRight } from 'lucide-react';
import InquiryModal from '../components/InquiryModal';

const Resources = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [buttonContext, setButtonContext] = useState('');

  const handleButtonClick = (context) => {
    setButtonContext(context);
    setIsModalOpen(true);
  };
  
  const categories = ['All', 'Revenue Growth', 'Market Protection', 'Brand Strategy', 'Legal Defense', 'Investor Appeal', 'Case Studies'];
  
  const articles = [
    {
      category: 'Revenue Growth',
      title: 'How Trademark Registration Unlocks New Revenue Streams',
      desc: 'Discover how protected brands command 30-40% price premiums, unlock Amazon Brand Registry benefits, and create licensing opportunities that generate passive income.',
      readTime: '6 min read',
      date: 'January 15, 2026',
      impact: 'Revenue Impact'
    },
    {
      category: 'Market Protection',
      title: 'Stop Competitors From Stealing Your Trademark Application Hub',
      desc: 'Learn proven strategies to eliminate marketplace copycats, prevent customer confusion, and defend your market position with legal protection.',
      readTime: '8 min read',
      date: 'January 10, 2026',
      impact: 'Risk Reduction'
    },
    {
      category: 'Brand Strategy',
      title: 'Building Brand Equity That Investors Will Pay For',
      desc: 'Why protected IP increases business valuation by 3x and makes your company acquisition-ready. Real data from successful exits.',
      readTime: '7 min read',
      date: 'January 5, 2026',
      impact: 'Valuation Boost'
    },
    {
      category: 'Legal Defense',
      title: 'Avoid $50K+ Legal Disputes: File Your Trademark Early',
      desc: 'Case studies of businesses that faced expensive rebranding and litigation because they delayed trademark registration. Learn from their mistakes.',
      readTime: '9 min read',
      date: 'December 28, 2024',
      impact: 'Cost Savings'
    },
    {
      category: 'Investor Appeal',
      title: 'What VCs Look For: IP Protection as a Deal Maker',
      desc: 'Inside perspective from venture capital firms on why protected intellectual property is a critical factor in funding decisions.',
      readTime: '6 min read',
      date: 'December 20, 2024',
      impact: 'Funding Success'
    },
    {
      category: 'Case Studies',
      title: 'From Startup to $10M: How IP Protection Fueled Growth',
      desc: 'Real success story of an eCommerce brand that leveraged trademark protection to eliminate competitors, raise funding, and 10x revenue in 3 years.',
      readTime: '12 min read',
      date: 'December 15, 2024',
      impact: 'Success Story'
    },
    {
      category: 'Brand Strategy',
      title: 'Amazon Brand Registry: Why Sellers Pay Premium for Trademarks',
      desc: 'The business case for Brand Registry—increased buy box wins, advertising tools, and protection against hijackers worth thousands monthly.',
      readTime: '8 min read',
      date: 'December 10, 2024',
      impact: 'Amazon Growth'
    },
    {
      category: 'Revenue Growth',
      title: 'Licensing Your Trademark: Create Passive Income Streams',
      desc: 'Step-by-step guide to monetizing your brand through licensing agreements. Real examples of businesses earning $10K-$100K annually in royalties.',
      readTime: '10 min read',
      date: 'December 5, 2024',
      impact: 'Passive Income'
    },
    {
      category: 'Market Protection',
      title: 'Cease & Desist: When and How to Enforce Your Trademark',
      desc: 'Practical guide to stopping infringers, protecting your market share, and avoiding costly litigation. Includes letter templates and enforcement strategies.',
      readTime: '7 min read',
      date: 'November 30, 2024',
      impact: 'Legal Tools'
    }
  ];

  const filteredArticles = activeFilter === 'All' 
    ? articles 
    : articles.filter(article => article.category === activeFilter);

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section - Market Oriented */}
      <section className="relative bg-gradient-to-br from-[#1A3628] via-[#11241A] to-[#1A3628] py-24 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-10">
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-block mb-6">
            <span className="text-[#C85A40] text-sm font-bold uppercase tracking-widest px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border-2 border-[#C85A40]/50">
              Business Growth Insights
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-8 leading-tight tracking-tight">
            Smart Strategies to
            <br />
            <span className="text-[#C85A40]">Protect & Scale</span>
          </h1>
          <p className="text-base text-gray-100 max-w-4xl mx-auto leading-relaxed font-light">
            Actionable insights on trademark strategy, brand protection, and leveraging IP for competitive advantage. Learn how successful businesses use intellectual property to drive revenue and market dominance.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-gradient-to-b from-white to-gray-50 border-b-2 border-gray-200 sticky top-16 z-40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <p className="text-sm font-bold text-[#C85A40] uppercase tracking-wider">Filter by Topic</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveFilter(category)}
                className={`px-6 py-3 rounded-xl font-bold text-sm ${
                  activeFilter === category
                    ? 'bg-gradient-to-r from-[#1A3628] to-[#C85A40] text-white shadow-xl scale-110 border-2 border-[#C85A40]'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200 hover:border-[#C85A40] shadow-md'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article, index) => (
              <Card key={index} className="group relative overflow-hidden border-2 border-gray-200 hover:border-[#C85A40] bg-white">
                {/* Hover Gradient Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#C85A40]/0 to-[#C85A40]/0 group-hover:from-[#C85A40]/5 group-hover:to-[#C85A40]/5"></div>
                
                <div className="relative z-10">
                  {/* Category & Impact Badge */}
                  <div className="p-6 pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <span className="inline-block px-4 py-1.5 bg-gradient-to-r from-[#1A3628] to-[#C85A40] text-white text-xs font-bold rounded-full uppercase tracking-wider">
                        {article.category}
                      </span>
                      <span className="inline-block px-3 py-1 bg-[#C85A40]/20 text-[#C85A40] text-xs font-bold rounded-full">
                        {article.impact}
                      </span>
                    </div>
                  </div>
                  
                  <div className="px-6 pb-6">
                    <h3 className="text-xl font-black text-[#1A3628] mb-4 leading-tight min-h-[60px]">
                      {article.title}
                    </h3>
                    <p className="text-gray-700 mb-6 leading-relaxed text-[15px]">
                      {article.desc}
                    </p>
                    
                    {/* Meta Info & Read More */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1.5 text-[#C85A40]" />
                          <span className="font-medium">{article.readTime}</span>
                        </div>
                      </div>
                      <button className="flex items-center gap-2 text-[#C85A40] font-bold text-sm group-hover:gap-3">
                        Read More
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA Section */}
      <section className="relative py-24 bg-gradient-to-br from-[#1A3628] via-[#11241A] to-[#1A3628] overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-10">
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-12 border border-white/10 shadow-2xl">
            <div className="inline-block mb-6">
              <span className="text-[#C85A40] text-sm font-bold uppercase tracking-widest px-6 py-3 bg-white/10 rounded-full border border-[#C85A40]/50">
                Weekly Business Insights
              </span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-black text-white mb-6 tracking-tight">
              Get Revenue-Driving Strategies
              <br />
              <span className="text-[#C85A40]">Delivered Every Week</span>
            </h2>
            <p className="text-base text-gray-200 mb-10 max-w-2xl mx-auto leading-relaxed">
              Join 10,000+ business owners receiving exclusive insights on brand protection, market strategy, and leveraging IP for competitive advantage.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto mb-6">
              <Input
                type="email"
                placeholder="Enter your business email"
                className="flex-1 px-6 py-6 text-base rounded-xl border-2 border-white/20 bg-white/10 text-white placeholder:text-gray-300 focus:border-[#C85A40] focus:bg-white/20 backdrop-blur-sm"
              />
              <Button
                onClick={() => handleButtonClick('Get Free Insights - Resources CTA')}
                className="bg-gradient-to-r from-[#C85A40] to-[#A64730] hover:from-[#A64730] hover:to-[#C85A40] text-[#1A3628] shadow-2xl"
              >
                Get Free Insights
              </Button>
            </div>
            
            <p className="text-sm text-gray-300">
              ✓ No spam • ✓ Unsubscribe anytime • ✓ Proven strategies from 10,000+ protected brands
            </p>
          </div>
        </div>
      </section>

      <InquiryModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        buttonContext={buttonContext}
      />
    </div>
  );
};

export default Resources;